'use client';

import { useEffect, useRef, useState } from 'react';

import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { stripTags } from '@eliadmin/core/src/utils/nt/text';

/**
 * Figma „Tabuľka“ (250:15261): 1 px #E2E5DF frame with radius 12, the head row on #F5F6F3 in Body/
 * Caption Semibold #5D655D, rows of 48 px with a bottom line, cells 14 px high and 16 px apart (20 px
 * at the sides), the row heading (first column) in Body/M Bold. On phones (310:17731) the cells keep
 * the Figma column widths, so a wide table scrolls sideways.
 */
const TABLE = [
  'overflow-x-auto overscroll-x-contain rounded-xl border border-[#E2E5DF]',
  'focus-visible:shadow-focus focus-visible:outline-none',
  '[&_table]:w-full [&_table]:border-collapse',
  '[&_tr]:border-b [&_tr]:border-[#E2E5DF] [&_tbody_tr:last-child]:border-b-0',
  '[&_:is(td,th)]:px-2 [&_:is(td,th)]:py-3.5 [&_:is(td,th)]:text-left [&_:is(td,th)]:align-top',
  '[&_:is(td,th):first-child]:pl-5 [&_:is(td,th):last-child]:pr-5',
  'max-md:[&_:is(td,th)]:min-w-[136px] max-md:[&_:is(td,th):first-child]:min-w-[170px]',
  '[&_td]:nt-body-m [&_td]:text-[#0D110D] max-md:[&_td]:text-[12px]',
  '[&_th]:nt-body-m-bold [&_th]:text-[#0D110D] max-md:[&_th]:text-[12px]',
  '[&_thead_th]:nt-caption-semibold [&_thead_th]:bg-[#F5F6F3] [&_thead_th]:leading-[1.2] [&_thead_th]:text-[#5D655D] max-md:[&_thead_th]:text-[10px]',
  '[&_:is(strong,b)]:font-bold',
  '[&_a]:font-semibold [&_a]:text-[#0E6A20] [&_a]:underline [&_a]:underline-offset-2',
  '[&_a]:rounded-[4px] [&_a:focus-visible]:shadow-focus [&_a:focus-visible]:outline-none',
].join(' ');

/**
 * A table of the post. While it is wider than the column it scrolls by keyboard too (a focusable
 * region) and shows the hint „Tabuľku posuňte do strany“ (Figma 310:17731 „Hint“) under it.
 */
export function ArticleTable({
  html,
  caption,
  hint,
}: {
  html: string;
  /** `<figcaption>` of the editor table (HTML), '' without one. */
  caption: string;
  hint: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const measure = () => setOverflows(scroller.scrollWidth > scroller.clientWidth + 1);
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);
    return () => observer.disconnect();
  }, []);

  const name = stripTags(caption) || hint;

  return (
    <figure className="mt-3 flex min-w-0 flex-col gap-2 md:mt-6">
      <div
        ref={scrollRef}
        tabIndex={overflows ? 0 : undefined}
        role={overflows ? 'region' : undefined}
        aria-label={overflows && name ? name : undefined}
        className={TABLE}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {overflows && hint ? (
        <p className="nt-caption flex items-center gap-1.5 text-[#5D655D] max-md:text-[10px]">
          <Icon name="hand-swipe-left" size={16} className="text-[#8F978E]" />
          {hint}
        </p>
      ) : null}
      {caption ? (
        <figcaption
          className="nt-caption text-[#5D655D] max-md:text-[10px]"
          dangerouslySetInnerHTML={{ __html: caption }}
        />
      ) : null}
    </figure>
  );
}
